import type * as amplitudeSdk from '@amplitude/analytics-browser';
import { ANALYTICS_EVENTS } from '@neke/core';
import type {
  AnalyticsEventName,
  AnalyticsProps,
  AnalyticsUserProps,
  Locale,
  TrackArgs,
  UserDto,
} from '@neke/core';

/**
 * Продуктовая аналитика.
 *
 * Ключ подставляется на сборке (VITE_*). Без ключа модуль молчит, а сам SDK
 * в бандл не попадает: сборщик видит, что до него не дойти, и выбрасывает код.
 *
 * SDK грузится отдельным чанком уже после первого рендера — он весит около
 * 65 КБ в gzip, а первый экран открывают с телефона. События, случившиеся до
 * загрузки, ждут в очереди и уходят следом.
 *
 * Автозахват кликов и форм выключен намеренно. Приложение работает с
 * персональными данными (вера, прошлый брак, контакты), а ссылка на отчёт
 * пары — это секрет: токен из адреса нельзя отдавать наружу ни в событии,
 * ни в имени страницы. Поэтому события заводим руками, а путь нормализуем.
 *
 * Здесь только транспорт. Имена событий и их свойства живут в словаре
 * `@neke/core` (`packages/core/src/analytics.ts`) — тот же словарь берёт
 * мобильное приложение, чтобы шаги воронки на вебе и в телефоне сходились в
 * одном отчёте, а не расползались по похожим именам.
 */

const API_KEY = import.meta.env.VITE_AMPLITUDE_API_KEY ?? '';
// По умолчанию EU — там заведён наш проект. С неверной зоной Amplitude
// отвечает «Invalid API key», и события теряются молча.
const SERVER_ZONE = import.meta.env.VITE_AMPLITUDE_SERVER_ZONE === 'US' ? 'US' : 'EU';

type Amplitude = typeof amplitudeSdk;

let sdk: Amplitude | null = null;
let failed = false;

/** Очередь ограничена: если SDK не загрузился, память копить незачем. */
const QUEUE_LIMIT = 50;
const queue: Array<(sdk: Amplitude) => void> = [];

export function initAnalytics(): void {
  if (!API_KEY) return;

  void import('@amplitude/analytics-browser')
    .then((amplitude) => {
      amplitude.init(API_KEY, {
        serverZone: SERVER_ZONE,
        // Из автозахвата оставляем только то, что нельзя собрать руками:
        // источник перехода и границы сессии.
        autocapture: {
          attribution: true,
          sessions: true,
          pageViews: false,
          formInteractions: false,
          fileDownloads: false,
          elementInteractions: false,
        },
        // Гео и адрес не собираем: город и так есть в анкете, а IP — лишнее.
        trackingOptions: { ipAddress: false },
      });
      sdk = amplitude;
      for (const job of queue.splice(0)) job(amplitude);
    })
    .catch(() => {
      // Блокировщик рекламы или обрыв сети. Приложение это не касается.
      failed = true;
      queue.length = 0;
    });
}

function withSdk(job: (sdk: Amplitude) => void): void {
  if (!API_KEY || failed) return;
  if (sdk) {
    job(sdk);
    return;
  }
  if (queue.length < QUEUE_LIMIT) queue.push(job);
}

/** Дольше этого никого не держим: событие важно, но не важнее перехода. */
const SEND_TIMEOUT_MS = 1500;

/**
 * Возвращает промис отправки: перед уходом со страницы (`location.replace`,
 * переход в Kaspi) его нужно дождаться, иначе событие не успеет уйти.
 * Промис всегда завершается — по ответу сервера или по таймауту.
 *
 * Имя события берём из `ANALYTICS_EVENTS`, а свойства проверяет словарь: лишнее
 * поле или забытое обязательное компилятор не пропустит.
 */
export function track<E extends AnalyticsEventName>(
  event: E,
  ...args: TrackArgs<E>
): Promise<void> {
  if (!API_KEY || failed) return Promise.resolve();

  const [props] = args as [AnalyticsProps?];

  const sent = new Promise<void>((resolve) => {
    withSdk((amplitude) => {
      void amplitude
        .track(event, props ? clean(props) : undefined)
        .promise.then(() => resolve(), () => resolve());
    });
  });

  return Promise.race([sent, delay(SEND_TIMEOUT_MS)]);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** undefined в свойствах Amplitude показывает как «(none)» — убираем заранее. */
function clean(props: AnalyticsProps): Record<string, string | number | boolean | null> {
  return Object.fromEntries(Object.entries(props).filter(([, v]) => v !== undefined)) as Record<
    string,
    string | number | boolean | null
  >;
}

/**
 * Связывает устройство с пользователем. id берём из базы, а не почту:
 * почта — персональные данные, и в аналитике ей делать нечего.
 */
export function identifyUser(user: UserDto): void {
  withSdk((amplitude) => amplitude.setUserId(user.id));
  setUserProps({
    locale: user.locale,
    sex: user.sex ?? 'unknown',
    role: user.role,
    has_consent: user.hasConsent,
    has_profile: user.hasProfile,
    opt_in_matching: user.optInMatching,
  });
}

/** Имена свойств пользователя тоже из словаря — их видно в фильтрах Amplitude. */
function setUserProps(props: Partial<AnalyticsUserProps>): void {
  withSdk((amplitude) => {
    const identify = new amplitude.Identify();
    for (const [key, value] of Object.entries(props)) {
      if (value !== undefined) identify.set(key, value);
    }
    amplitude.identify(identify);
  });
}

/** Выход: следующие события уходят как анонимные, с новым device id. */
export function resetUser(): void {
  withSdk((amplitude) => amplitude.reset());
}

export function setUserLocale(locale: Locale): void {
  setUserProps({ locale });
}

/**
 * Приводит адрес к шаблону маршрута: /pair/aBcD… → /pair/:token.
 * В аналитику уходит только шаблон — идентификаторы пары и платежа остаются
 * в браузере.
 */
export function routeOf(pathname: string): string {
  const rules: Array<[RegExp, string]> = [
    [/^\/pair\/id\/[^/]+$/, '/pair/id/:pairId'],
    [/^\/pair\/[^/]+$/, '/pair/:token'],
    [/^\/payment\/[^/]+$/, '/payment/:id'],
  ];
  for (const [re, route] of rules) {
    if (re.test(pathname)) return route;
  }
  return pathname;
}

export function trackPageView(pathname: string): void {
  void track(ANALYTICS_EVENTS.pageViewed, { route: routeOf(pathname) });
}
